<template>
  <b-modal
    id="modal-search-coupon"
    title="選擇優惠券"
    @show="initializeTempCoupons"
    no-close-on-backdrop
  >
    <b-input-group>
      <b-form-input placeholder="請輸入優惠券名稱" v-model="query.keyword" @keyup.enter="fetchCoupons(1)"></b-form-input>
      <b-input-group-append>
        <b-button @click="fetchCoupons(1)"><i class="fa fa-search"></i></b-button>
      </b-input-group-append>
    </b-input-group>
    <div class="row mt-3">
      <div class="col-12 d-flex align-items-center">
        <label class="mb-0 mr-2" style="min-width: 80px;">分店：</label>
        <AppSingleSelect
          v-model="selectedBranch"
          :options="branchOptions"
          @input="branchChange"
          :disabled="disableBranchSelect"
          class="flex-fill"
        ></AppSingleSelect>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 d-flex align-items-center">
        <label class="mb-0 mr-2" style="min-width: 80px;">領取方式：</label>
        <b-form-select
          v-model="query.take_type"
          :options="takeTypes"
          class="flex-fill"
        ></b-form-select>
      </div>
    </div>

    <div class="text-center mt-3 mb-3 loading-spinner" v-if="isLoading">
      <b-spinner variant="secondary" small />
    </div>
    <div class="mt-3 mb-3" v-else-if="coupons.length > 0">
      <div
        v-for="coupon in coupons"
        v-bind:key="coupon.redeem_code_bundle_id"
        class="d-flex align-items-center py-2 coupon-item"
        @click="handleSelected(coupon)"
      >
        <template>
          <b-img-lazy
            v-if="coupon.img_url"
            class="mx-2 thumb-image flex-shrink-0"
            :src="coupon.img_url"
            alt="優惠券圖片"
            width="50"
            height="25"
          />
          <div v-else class="mx-2 flex-shrink-0" :style="{ width: '50px', height: '25px'}"></div>
        </template>
        <div class="mr-auto text-nowrap text-truncate">
          <div class="font-weight-bold">
            {{ coupon.name }}
          </div>
          <small class="text-muted">
            {{ format(new Date(coupon.start_at), 'yyyy-MM-dd HH:mm') }} ~ {{ format(new Date(coupon.end_at), 'yyyy-MM-dd HH:mm') }}
          </small>
        </div>
        <div class="mx-2">
          <span v-if="selectedCouponIds.includes(coupon.id)" class="mdi mdi-check-circle mdi-24px" />
        </div>
      </div>
    </div>

    <template #modal-footer>
      <div class="w-100 d-flex">
        <div class="pagination mr-auto d-flex align-items-center">
          <a class="mr-2 arrow-point" @click="navigateToPage(1)">
            <IconNavigateToFirst :fill="meta.current_page === 1 ? '#ccc' : 'black'" />
          </a>
          <a class="mr-2 arrow-point" @click="navigateToPage(meta.current_page - 1)">
            <IconAngleLeft :fill="meta.current_page === 1 ? '#ccc' : 'black'" />
          </a>
          <span class="mr-2 d-inline-block page-number" @click.prevent="navigateToPage(meta.current_page)">
            {{ meta.current_page }} / {{ meta.last_page }}
          </span>
          <a class="mr-2 arrow-point" @click="navigateToPage(meta.current_page + 1)">
            <IconAngleRight :fill="meta.current_page === meta.last_page ? '#ccc' : 'black'" />
          </a>
          <a class="arrow-point" @click="navigateToPage(meta.last_page)">
            <IconNavigateToLast :fill="meta.current_page === meta.last_page ? '#ccc' : 'black'" />
          </a>
        </div>

        <b-button variant="danger" size="sm" class="mr-2 ml-3" @click="closeModal">
          取消
        </b-button>
        <b-button variant="primary" size="sm" @click="submitSelectedCoupon">
          確定
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { format } from "date-fns";
import redeemCodeBundleApi from "@/apis/redeemCodeBundle";
import redeemCodeBundleFiltersMixin from "@/mixins/redeemCodeBundleFilters";
import AppSingleSelect from "@/components/AppSingleSelect.vue";
import IconAngleLeft from '@/components/Icon/IconAngleLeft.vue';
import IconAngleRight from '@/components/Icon/IconAngleRight.vue';
import IconNavigateToLast from '@/components/Icon/IconNavigateToLast.vue';
import IconNavigateToFirst from '@/components/Icon/IconNavigateToFirst.vue';

export default {
  components: {
    AppSingleSelect,
    IconAngleLeft,
    IconAngleRight,
    IconNavigateToLast,
    IconNavigateToFirst,
  },
  mixins: [redeemCodeBundleFiltersMixin],
  props: {
    mode: {
      type: String,
      default: "single", // enum: single, multiple
    },
    selectedCoupon: {
      type: Object,
      default: null,
    },
    selectedCoupons: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      coupons: [],
      localCoupons: [],
      meta: {},
      query: {
        page: 1,
        per_page: 10,
        merchant_id: null,
        create_type: "0",
        keyword: "",
        branch_id: null,
        take_type: "",
      },
      disableBranchSelect: !this.$permissions.has(this.$permissions.consts.REDEEM_CODE_LIST_BRANCH)
    };
  },
  computed: {
    selectedCouponIds() {
      return this.localCoupons.map((coupon) => coupon.id)
    },
  },

  watch: {
    "query.take_type"() {
      this.query.page = 1;
      this.fetchCoupons();
    },
  },

  created() {
    this.fetchCoupons();
  },

  methods: {
    format,
    branchChange(selectedBranch) {
      this.query.branch_id = selectedBranch ? selectedBranch.value : "";
      this.query.page = 1;
      this.fetchCoupons();
    },
    async fetchCoupons(page = null) {
      if (page) {
        this.query.page = page;
      }

      // 避免使用者沒有跟選任何分店
      // 當沒有 branch_id 但是沒有無分店權限則自動帶入預設分店
      if (
        !this.query.branch_id &&
        !this.$permissions.has(this.$permissions.consts.REDEEM_CODE_LIST_NO_BRANCH)
      ) {
        this.query.branch_id = this.currentBranch.id;
        this.selectedBranch = this.branchOptions.find(
          (option) => option.value === this.currentBranch.id
        );
      }

      try {
        this.isLoading = true;
        const params = {
          ...this.query,
        };
        const { data } = await redeemCodeBundleApi.getRedeemCodeBundles(params);
        this.meta = data.meta;
        this.coupons = data.data;
      } catch (error) {
        console.log("Error fetching coupons:", error);
      }
      this.isLoading = false;
    },

    // 初始化時設置選擇的優惠券
    initializeTempCoupons() {
      if (this.mode === 'single' && this.selectedCoupon) {
        this.localCoupons = [this.selectedCoupon];
      } else {
        this.localCoupons = this.selectedCoupons;
      }
    },

    handleSelected(coupon) {
      const index = this.localCoupons.findIndex(({ id }) => id === coupon.id);

      if (index === -1) {
        if (this.mode === 'single') {
          this.localCoupons = [coupon];
        } else {
          this.localCoupons.push(coupon);
        }
      } else {
        this.localCoupons.splice(index, 1);
      }
    },

    submitSelectedCoupon() {
      this.$bvModal.hide('modal-search-coupon');

      if (this.localCoupons.length)  {
        this.$emit(
          'submit',
          this.mode === 'single' ? this.localCoupons[0] : this.localCoupons
        );
      }
    },

    closeModal() {
      this.$bvModal.hide('modal-search-coupon');
    },

    navigateToPage(pageNumber) {
      if (pageNumber < 1 || pageNumber > this.meta.last_page) {
        return;
      }
      this.query.page = pageNumber;
      this.fetchCoupons();
    }
  }
}
</script>

<style lang="scss">
.coupon-item:hover {
  cursor: pointer;
  background-color: #c7c7c7;
  color: white;
  width: 100%;

  .staff-list-info {
    color: white;
  }
}

.arrow-point:hover {
  cursor: pointer;
}

.page-number {
  color: #888;
  min-width: 3rem;
  text-align: center;
}
</style>
